import styled, { css } from "styled-components";
import * as Button from "./Button";
import { getBreakpoint } from "../../themes/common";
import { toyotaTheme } from "../../themes/toyotaTheme";

type ListItemSizesType = {
    WIDTH: number;
    MARGIN: number;
    DIVIDER_WIDTH: number;
    TOTAL_WIDTH: number;
};

export const getListItemSizes = (isLarge: boolean): ListItemSizesType => {
    const WIDTH = isLarge ? 100 : 50;
    const MARGIN = isLarge ? 20 : 10;
    const DIVIDER_WIDTH = MARGIN;
    const TOTAL_WIDTH = WIDTH + MARGIN * 2 + DIVIDER_WIDTH;

    return {
        WIDTH,
        MARGIN,
        DIVIDER_WIDTH,
        TOTAL_WIDTH,
    };
};

type ExteriorImageType = {
    image?: string;
    rgb?: string;
    isSelected?: boolean;
    large: boolean;
    zoomedIn: boolean;
    lightColour: boolean;
};

export const ExteriorImage = styled.div<ExteriorImageType>`
    position: relative;
    top: 0;
    width: ${({ large }) => getListItemSizes(large).WIDTH}px;
    height: ${({ large }) => getListItemSizes(large).WIDTH}px;
    margin: 0 ${({ large }) => getListItemSizes(large).MARGIN}px;
    background-color: transparent;
    border-radius: 50%;
    ${({ image, large, zoomedIn }) =>
        image &&
        css`
            background-image: url(${image});
            background-position: center;
            background-size: ${large || !zoomedIn ? "100%" : "55px"};
        `};
    ${({ rgb, image }) =>
        rgb &&
        !image &&
        css`
            background-color: ${`#${rgb.substr(0, 2) === "0x" ? rgb.substr(2) : rgb}`};
        `};

    &::before {
        position: absolute;
        top: -4px;
        left: -4px;
        width: calc(100% + 8px);
        height: calc(100% + 8px);
        content: "";
        border: 1px solid transparent;
        border-color: ${({ isSelected }) => (isSelected ? toyotaTheme.color.grey2 : "transparent")};
        border-radius: 50%;
        transition: border-color 0.3s;
    }

    svg {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 40%;
        height: 40%;
        transform: translate(-50%, -50%);
    }
`;

export const ItemWrapper = styled(Button.Link)<{
    showDivider?: boolean;
    large: boolean;
    extraPadding: number;
    multiLine?: boolean;
}>`
    margin: 0 ${({ extraPadding }) => extraPadding}px;
    ${({ showDivider, large }) =>
        showDivider &&
        css`
            position: relative;
            &::before {
                position: absolute;
                bottom: 0;
                left: -${getListItemSizes(large).DIVIDER_WIDTH / 2}px;
                width: 1px;
                height: ${getListItemSizes(large).WIDTH}px;
                padding: 0 0.5px;
                content: "";
                background-color: ${toyotaTheme.color.grey2};
                box-sizing: border-box;
            }
        `};

    html[dir="rtl"] & {
        ${({ showDivider, large }) =>
            showDivider &&
            css`
                &::before {
                    left: auto;
                    right: -${getListItemSizes(large).DIVIDER_WIDTH / 2}px;
                }
            `};
    }
`;

export const ExteriorType = styled.div<{ show: boolean; hasLongText?: boolean }>`
    position: relative;
    max-width: 80px;
    min-height: 2rem;
    max-height: 4rem;
    margin-bottom: ${toyotaTheme.space[2]}px;
    padding: 0 10px;
    font-size: 1.4rem;
    line-break: anywhere;
    line-height: 2rem;
    text-align: start;
    color: ${toyotaTheme.color.grey4};
    transition: opacity 0.24s;
    opacity: ${({ show }) => (show ? 1 : 0)};

    ${({ hasLongText }) =>
        hasLongText &&
        css`
            font-size: 1.1rem;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
        `};
`;

export const Wrapper = styled.div`
    position: relative;
`;

export const Slider = styled.div`
    flex: 1;
    overflow: hidden;
    user-select: none;
`;

export const SliderViewport = styled.div<{ width?: number; large: boolean }>`
    display: flex;
    position: relative;
    align-items: center;
    width: calc(100% - 80px);
    height: ${({ large }) => (large ? 120 : 106)}px;
    overflow: hidden;

    @media ${getBreakpoint("down", "lg")} {
        width: 100%;
    }

    ${Button.Link} {
        width: ${({ large }) => getListItemSizes(large).TOTAL_WIDTH}px;
        min-width: ${({ large }) => getListItemSizes(large).TOTAL_WIDTH}px;
        text-align: center;
    }
`;

export const Track = styled.div<{ width: number; enableTransition: boolean }>`
    display: flex;
    position: absolute;
    top: 0px; /* This might seem like a unnecessary property, but our friend IE11 thinks this is very important */
    flex-direction: row;
    align-items: flex-end;
    ${({ width }) => css`
        width: ${width}px;
    `};
    ${({ enableTransition }) =>
        enableTransition &&
        css`
            transition:
                left 0.5s,
                right 0.5s;
        `};
`;

export const SliderButton = styled.button<{ alignment: string; show?: boolean }>`
    position: absolute;
    top: 50%;
    width: 40px;
    height: 40px;
    margin: 0;
    padding: 0;
    background-color: transparent;
    border: 0;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -ms-transform: translateY(-50%) translateY(10px); /* Fix for IE11 */
    transform: translateY(calc(-50% + 10px));
    transition: opacity 0.24s;
    svg {
        color: ${toyotaTheme.color.primary};
    }
    ${({ show }) =>
        !show &&
        css`
            visibility: hidden;
        `};
    ${({ alignment, theme }) =>
        alignment === "left"
            ? css`
                  ${alignment}: ${theme.isRTL ? 0 : "-80px"};
              `
            : css`
                  ${alignment}: ${theme.isRTL ? "-80px" : 0};
              `};

    @media ${getBreakpoint("down", "lg")} {
        display: none;
    }
`;
